import { db } from "../firebase";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function isLeapYear(year) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function isValidDayMonthYear(day, month, year) {
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
    return false;
  }
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;

  const daysInMonth = [
    31,
    isLeapYear(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];

  return day <= daysInMonth[month - 1];
}

/**
 * Accepts:
 * - "dd-mm-yyyy"
 * - "yyyy-mm-dd" (useful from <input type="date" />)
 * - Date
 *
 * Returns:
 * - "dd-mm-yyyy"
 */
export function normalizeCalendarDayId(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = pad2(value.getDate());
    const month = pad2(value.getMonth() + 1);
    const year = String(value.getFullYear());
    return `${day}-${month}-${year}`;
  }

  const raw = String(value || "").trim();
  if (!raw) throw new Error("Calendar day id is required.");

  // dd-mm-yyyy
  let m = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) {
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = Number(m[3]);

    if (!isValidDayMonthYear(day, month, year)) {
      throw new Error(`Invalid calendar day id: ${raw}`);
    }

    return `${pad2(day)}-${pad2(month)}-${year}`;
  }

  // yyyy-mm-dd
  m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);

    if (!isValidDayMonthYear(day, month, year)) {
      throw new Error(`Invalid calendar day id: ${raw}`);
    }

    return `${pad2(day)}-${pad2(month)}-${year}`;
  }

  throw new Error(
    `Calendar day id must be dd-mm-yyyy (or yyyy-mm-dd from a date input). Received: ${raw}`
  );
}

export function calendarDayIdToISO(id) {
  const normalized = normalizeCalendarDayId(id);
  const [day, month, year] = normalized.split("-");
  return `${year}-${month}-${day}`;
}

function sanitizeCelebrations(items) {
  const arr = Array.isArray(items) ? items : [];

  return arr
    .map((item) => {
      if (typeof item === "string") {
        const name = item.trim();
        return name ? { name, color: "" } : null;
      }

      if (!item || typeof item !== "object") return null;

      const name = String(item.name || "").trim();
      const color = String(item.color || "").trim();

      if (!name) return null;

      return { name, color };
    })
    .filter(Boolean);
}

function buildCalendarDayData(id, payload = {}, { forCreate = false } = {}) {
  const dayId = normalizeCalendarDayId(id);

  const data = {
    date: dayId,
    dateISO: calendarDayIdToISO(dayId),
    updatedAt: serverTimestamp(),
  };

  if (forCreate || "celebrations" in payload) {
    data.celebrations = sanitizeCelebrations(payload.celebrations);
  }

  if (forCreate || "feast" in payload) {
    data.feast = !!payload.feast;
  }

  if (forCreate || "solemnity" in payload) {
    data.solemnity = !!payload.solemnity;
  }

  if (forCreate) {
    if (!("celebrations" in data)) data.celebrations = [];
    if (!("feast" in data)) data.feast = false;
    if (!("solemnity" in data)) data.solemnity = false;
    data.createdAt = serverTimestamp();
  }

  return { dayId, data };
}

export async function fetchCalendarDay(id) {
  const dayId = normalizeCalendarDayId(id);
  const snap = await getDoc(doc(db, "liturgical", dayId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function fetchAllCalendarDays() {
  const snap = await getDocs(
    query(collection(db, "liturgical"), orderBy("dateISO"))
  );

  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function upsertCalendarDay(id, payload, { merge = true } = {}) {
  const { dayId, data } = buildCalendarDayData(id, payload, { forCreate: false });
  await setDoc(doc(db, "liturgical", dayId), data, { merge });
  return data;
}

export async function createCalendarDay(id, payload = {}) {
  const { dayId, data } = buildCalendarDayData(id, payload, { forCreate: true });
  await setDoc(doc(db, "liturgical", dayId), data, { merge: false });
  return data;
}

export async function deleteCalendarDay(id) {
  const dayId = normalizeCalendarDayId(id);
  await deleteDoc(doc(db, "liturgical", dayId));
  return { ok: true, id: dayId };
}